import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Database, X, Check, AlertCircle, Loader2, ChevronRight } from 'lucide-react';
import apiClient from '../../api/client';
import { chemistryApi } from '../../api/chemistry';
import type { Project } from '../../types/chemistry';
import StructureImage from './StructureImage';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

type SaveType = 'properties' | 'structures' | 'attachments';
type Phase = 'preview' | 'configure' | 'saving' | 'done';

interface PropertyRow { key: string; value: string | number | boolean | null; unit?: string }
interface StructureRow { smiles: string; name?: string; properties?: Record<string, any> }
interface AttachmentRow { type: string; label?: string; format: string; data: string; metadata?: Record<string, any> }

export interface SaveResultsWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveComplete?: () => void;
  rawData: any;
  toolName: string;
  toolKey: string;
  smiles?: string;
  moleculeId?: number;
  fileId?: number;
  projectId?: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Detect save type from tool key and raw data
// ─────────────────────────────────────────────────────────────────────────

function detectSaveType(toolKey: string, rawData: any): SaveType {
  if (!rawData) return 'properties';

  // Structure-producing tools
  if (toolKey.startsWith('library_') || toolKey === 'library') return 'structures';
  if (rawData.enumerated_molecules || rawData.structures) return 'structures';

  // 3D/attachment-producing tools
  if (toolKey.startsWith('conformer') || toolKey.startsWith('dock') || toolKey.startsWith('quantum')) return 'attachments';
  if (rawData.conformers || rawData.pose_excerpt || rawData.modes) return 'attachments';

  // Default: scalar properties (ADMET, properties, calcs, etc.)
  return 'properties';
}

// ─────────────────────────────────────────────────────────────────────────
// Extract data rows from raw data
// ─────────────────────────────────────────────────────────────────────────

function extractProperties(toolKey: string, rawData: any): PropertyRow[] {
  if (!rawData || typeof rawData !== 'object') return [];
  const rows: PropertyRow[] = [];
  // Flatten all scalar values from the raw data
  const skip = new Set(['smiles', 'inchi', 'svg', 'molblock', 'pose_excerpt', 'modes', 'conformers', 'structures', 'enumerated_molecules']);
  for (const [key, val] of Object.entries(rawData)) {
    if (skip.has(key)) continue;
    if (val === null || val === undefined) continue;
    if (typeof val === 'object' && !Array.isArray(val)) continue;
    if (Array.isArray(val)) continue;
    rows.push({ key, value: val as string | number | boolean });
  }
  return rows;
}

function extractStructures(rawData: any): StructureRow[] {
  if (!rawData) return [];
  const mols = rawData.enumerated_molecules || rawData.structures || [];
  return mols.map((m: any) => ({
    smiles: m.smiles || m.SMILES || '',
    name: m.name || m.label || undefined,
    properties: m.properties || undefined,
  })).filter((m: StructureRow) => m.smiles);
}

function extractAttachments(toolKey: string, rawData: any): { attachments: AttachmentRow[]; scalarProps: PropertyRow[] } {
  const attachments: AttachmentRow[] = [];
  const scalarProps: PropertyRow[] = [];

  // Conformers
  if (rawData.conformers && Array.isArray(rawData.conformers)) {
    rawData.conformers.forEach((c: any, i: number) => {
      attachments.push({
        type: 'conformer',
        label: c.label || `Conformer ${i + 1}`,
        format: 'sdf',
        data: c.molblock || c.data || '',
        metadata: { energy: c.energy, forcefield: c.forcefield || rawData.forcefield },
      });
    });
    if (rawData.forcefield) scalarProps.push({ key: 'forcefield', value: rawData.forcefield });
    scalarProps.push({ key: 'count', value: rawData.conformers.length });
    if (rawData.conformers[0]?.energy != null) {
      scalarProps.push({ key: 'energy_min', value: Math.min(...rawData.conformers.map((c: any) => c.energy).filter((e: any) => e != null)) });
    }
  }

  // Docking poses
  if (rawData.modes && Array.isArray(rawData.modes)) {
    rawData.modes.forEach((m: any, i: number) => {
      if (m.pdbqt || m.molblock || m.data) {
        attachments.push({
          type: 'docked_pose',
          label: `Mode ${i + 1} (${m.affinity_kcal_mol ?? '?'} kcal/mol)`,
          format: m.pdbqt ? 'pdbqt' : 'sdf',
          data: m.pdbqt || m.molblock || m.data,
          metadata: { affinity_kcal_mol: m.affinity_kcal_mol, rmsd_lb: m.rmsd_lb, rmsd_ub: m.rmsd_ub },
        });
      }
    });
    if (rawData.best_affinity_kcal_mol != null) scalarProps.push({ key: 'best_affinity', value: rawData.best_affinity_kcal_mol, unit: 'kcal/mol' });
    if (rawData.receptor_label) scalarProps.push({ key: 'receptor', value: rawData.receptor_label });
    scalarProps.push({ key: 'num_modes', value: rawData.modes.length });
  }

  // Single pose excerpt
  if (rawData.pose_excerpt && !rawData.modes) {
    attachments.push({
      type: 'docked_pose',
      label: 'Docked Pose',
      format: 'pdb',
      data: rawData.pose_excerpt,
      metadata: { affinity_kcal_mol: rawData.best_affinity_kcal_mol },
    });
  }

  return { attachments, scalarProps };
}

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

const SaveResultsWizard: React.FC<SaveResultsWizardProps> = ({
  isOpen, onClose, onSaveComplete, rawData, toolName, toolKey, smiles, moleculeId, fileId, projectId,
}) => {
  const [phase, setPhase] = useState<Phase>('preview');
  const [overwrite, setOverwrite] = useState(false);
  const [newFileName, setNewFileName] = useState(`Workbench ${toolName}`);
  const [progress, setProgress] = useState(0);
  const [resultSummary, setResultSummary] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef(false);

  // Project selector state (for structures when no projectId prop, or properties/attachments when no context)
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(projectId ?? null);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // File selector state (for properties/attachments when no fileId)
  const [files, setFiles] = useState<{ id: string; fileName: string }[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(fileId ?? null);
  const [filesLoading, setFilesLoading] = useState(false);

  // Create new project state
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const saveType = detectSaveType(toolKey, rawData);

  // Whether we need the project/file picker (no project/file context)
  const needsContextPicker = (saveType === 'properties' || saveType === 'attachments') && (!fileId && !projectId);

  // The effective project ID: prop takes priority, then user selection
  const effectiveProjectId = projectId ?? selectedProjectId;

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setPhase('preview');
      setOverwrite(false);
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
      setNewFileName(`${toolName} ${timestamp}`);
      setProgress(0);
      setResultSummary(null);
      setError('');
      abortRef.current = false;
      setSelectedProjectId(projectId ?? null);
      setSelectedFileId(fileId ?? null);
      setFiles([]);
      setCreatingProject(false);
      setNewProjectName('');
    }
  }, [isOpen, toolName, projectId, fileId]);

  // Fetch projects when dialog opens and we need a project picker
  useEffect(() => {
    const needsProjects = (saveType === 'structures' && !projectId) || needsContextPicker;
    if (!isOpen || !needsProjects) return;
    let cancelled = false;
    setProjectsLoading(true);
    chemistryApi.getProjects()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        if (list.length === 1) setSelectedProjectId(list[0].id);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, saveType, projectId, needsContextPicker]);

  // Fetch files when project is selected (for context picker)
  useEffect(() => {
    if (!isOpen || !needsContextPicker || !selectedProjectId) return;
    let cancelled = false;
    setFilesLoading(true);
    setSelectedFileId(null);
    chemistryApi.getFiles(selectedProjectId)
      .then((list) => {
        if (cancelled) return;
        setFiles(list.map(f => ({ id: f.id, fileName: f.originalFileName || f.fileName || `File #${f.id}` })));
        if (list.length === 1) setSelectedFileId(Number(list[0].id));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFilesLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, needsContextPicker, selectedProjectId]);

  // Extract preview data — useMemo prevents unstable references that break useCallback
  const properties = useMemo(
    () => saveType === 'properties' ? extractProperties(toolKey, rawData) : [],
    [saveType, toolKey, rawData],
  );
  const structures = useMemo(
    () => saveType === 'structures' ? extractStructures(rawData) : [],
    [saveType, rawData],
  );
  const { attachments, scalarProps } = useMemo(
    () => saveType === 'attachments' ? extractAttachments(toolKey, rawData) : { attachments: [] as AttachmentRow[], scalarProps: [] as PropertyRow[] },
    [saveType, toolKey, rawData],
  );

  const totalItems = saveType === 'properties' ? properties.length
    : saveType === 'structures' ? structures.length
    : attachments.length;

  // ─── Save handler ───
  const handleSave = useCallback(async () => {
    setPhase('saving');
    setProgress(0);
    setError('');
    abortRef.current = false;

    try {
      let saveProjectId = effectiveProjectId;
      if (creatingProject && newProjectName.trim()) {
        const createResp = await apiClient.post('/projects', { name: newProjectName.trim() }, { timeout: 15000 });
        saveProjectId = createResp.data.id;
        setSelectedProjectId(saveProjectId);
        setCreatingProject(false);
      }

      let result: any;
      if (saveType === 'properties') {
        const effectiveFileId = fileId ?? selectedFileId;
        if (!effectiveFileId && !saveProjectId && !moleculeId) throw new Error('Please select a destination file or project');
        setProgress(50);
        const resp = await apiClient.post('/v2/workbench/save/properties', {
          molecule_id: moleculeId || undefined,
          molecule_smiles: smiles || undefined,
          file_id: effectiveFileId || undefined,
          project_id: saveProjectId || undefined,
          source_tool: toolKey.replace(/^(admet_|dock_|conformer_|quantum_|library_|spr_|kinetics_|ic50_)/, '').replace(/_predict$/, '') || toolKey,
          properties: properties.map(p => ({ key: p.key, value: p.value, unit: p.unit })),
          overwrite,
        }, { timeout: 30000 });
        result = resp.data;
      } else if (saveType === 'structures') {
        if (!saveProjectId) throw new Error('Please select a destination project');
        setProgress(30);
        const resp = await apiClient.post('/v2/workbench/save/structures', {
          project_id: saveProjectId,
          source_tool: toolKey,
          structures: structures.map(s => ({ smiles: s.smiles, name: s.name, properties: s.properties })),
          create_new_file: true,
          new_file_name: newFileName,
        }, { timeout: 60000 });
        result = resp.data;
      } else {
        const effectiveFileId = fileId ?? selectedFileId;
        if (!moleculeId || !effectiveFileId) throw new Error('Molecule and file context required to save attachments');
        setProgress(30);
        const resp = await apiClient.post('/v2/workbench/save/attachments', {
          molecule_id: moleculeId,
          file_id: effectiveFileId,
          source_tool: toolKey,
          attachments: attachments.map(a => ({ type: a.type, label: a.label, format: a.format, data: a.data, metadata: a.metadata })),
          scalar_properties: scalarProps.length > 0 ? scalarProps.map(p => ({ key: p.key, value: p.value, unit: p.unit })) : undefined,
        }, { timeout: 60000 });
        result = resp.data;
      }

      setProgress(100);
      setResultSummary(result);
      setPhase('done');
    } catch (err: any) {
      console.error('[SaveResultsWizard] Save failed:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Save failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setPhase('configure');
    }
  }, [saveType, moleculeId, fileId, effectiveProjectId, toolKey, properties, structures, attachments, scalarProps, overwrite, newFileName, creatingProject, newProjectName, selectedFileId, smiles]);

  if (!isOpen) return null;

  const typeLabel = saveType === 'properties' ? 'Properties' : saveType === 'structures' ? 'Structures' : '3D Attachments';

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 1200, background: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--card-bg, #1e1e2e)',
          border: '1px solid var(--card-border, #333)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 560,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Database size={18} style={{ color: '#f59e0b' }} />
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>
              Save {typeLabel} to Project
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {/* ── Phase: Preview ── */}
          {phase === 'preview' && (
            <div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 12 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{toolName}</strong> produced {totalItems} {saveType === 'properties' ? 'properties' : saveType === 'structures' ? 'structures' : 'attachments'} to save.
              </div>

              {saveType === 'properties' && (
                <div style={{ background: 'var(--hover-bg)', borderRadius: 8, border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                  {properties.slice(0, 20).map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: i < properties.length - 1 ? '1px solid var(--card-border)' : 'none' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'monospace' }}>wb_{toolKey}_{p.key}</span>
                      <span style={{ color: '#f59e0b', fontSize: 11, fontFamily: 'monospace' }}>{String(p.value)}{p.unit ? ` ${p.unit}` : ''}</span>
                    </div>
                  ))}
                  {properties.length > 20 && (
                    <div style={{ padding: '6px 12px', color: 'var(--text-muted)', fontSize: 11, textAlign: 'center' }}>...and {properties.length - 20} more</div>
                  )}
                </div>
              )}

              {saveType === 'structures' && (
                <div style={{ background: 'var(--hover-bg)', borderRadius: 8, border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                  {structures.slice(0, 10).map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: i < Math.min(structures.length, 10) - 1 ? '1px solid var(--card-border)' : 'none' }}>
                      <StructureImage smiles={s.smiles} size={36} />
                      {s.name && <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{s.name}</span>}
                    </div>
                  ))}
                  {structures.length > 10 && (
                    <div style={{ padding: '6px 12px', color: 'var(--text-muted)', fontSize: 11, textAlign: 'center' }}>...and {structures.length - 10} more</div>
                  )}
                </div>
              )}

              {saveType === 'attachments' && (
                <div style={{ background: 'var(--hover-bg)', borderRadius: 8, border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                  {attachments.slice(0, 10).map((a, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: i < Math.min(attachments.length, 10) - 1 ? '1px solid var(--card-border)' : 'none' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{a.label || a.type}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 11, fontFamily: 'monospace' }}>{a.format.toUpperCase()} ({Math.round(a.data.length / 1024)}KB)</span>
                    </div>
                  ))}
                  {scalarProps.length > 0 && (
                    <div style={{ padding: '8px 12px', borderTop: '1px solid var(--card-border)', color: 'var(--text-secondary)', fontSize: 11 }}>
                      + {scalarProps.length} scalar properties will also be saved
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Phase: Configure ── */}
          {phase === 'configure' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8 }}>
                  <AlertCircle size={14} style={{ color: '#fb7185' }} />
                  <span style={{ color: '#fb7185', fontSize: 12 }}>{error}</span>
                </div>
              )}

              {saveType === 'properties' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {needsContextPicker && (
                    <>
                      <div>
                        <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 6 }}>Destination project</label>
                        {projectsLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                            <Loader2 size={14} style={{ color: '#f59e0b', animation: 'spin 1s linear infinite' }} />
                            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading projects...</span>
                          </div>
                        ) : projects.length === 0 ? (
                          <div style={{ color: '#fb7185', fontSize: 12 }}>No projects found. Create a project first.</div>
                        ) : (
                          <select
                            value={creatingProject ? '__new__' : (selectedProjectId ?? '')}
                            onChange={(e) => {
                              if (e.target.value === '__new__') {
                                setCreatingProject(true);
                                setSelectedProjectId(null);
                              } else {
                                setCreatingProject(false);
                                setSelectedProjectId(e.target.value ? Number(e.target.value) : null);
                              }
                            }}
                            style={selectStyle}
                          >
                            <option value="">Select a project...</option>
                            {projects.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                            <option value="__new__">+ Create new project</option>
                          </select>
                        )}
                        {creatingProject && (
                          <div style={{ marginTop: 8 }}>
                            <input
                              type="text"
                              value={newProjectName}
                              onChange={(e) => setNewProjectName(e.target.value)}
                              placeholder="New project name"
                              style={{
                                width: '100%', padding: '8px 12px', background: 'var(--hover-bg)',
                                border: '1px solid var(--card-border)', borderRadius: 8,
                                color: 'var(--text-primary)', fontSize: 13,
                                outline: 'none', boxSizing: 'border-box' as const,
                              }}
                              autoFocus
                            />
                          </div>
                        )}
                      </div>
                      {selectedProjectId && !creatingProject && (
                        <div>
                          <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 6 }}>Destination file</label>
                          {filesLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                              <Loader2 size={14} style={{ color: '#f59e0b', animation: 'spin 1s linear infinite' }} />
                              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading files...</span>
                            </div>
                          ) : files.length === 0 ? (
                            <div style={{ color: '#fb7185', fontSize: 12 }}>No files in this project.</div>
                          ) : (
                            <select
                              value={selectedFileId ?? ''}
                              onChange={(e) => setSelectedFileId(e.target.value ? Number(e.target.value) : null)}
                              style={selectStyle}
                            >
                              <option value="">Select a file...</option>
                              {files.map((f) => (
                                <option key={f.id} value={f.id}>{f.fileName}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Overwrite existing wb_{toolKey}_* properties</span>
                  </label>
                </div>
              )}

              {saveType === 'structures' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Project selector — shown when no projectId prop */}
                  {!projectId && (
                    <div>
                      <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 6 }}>Destination project</label>
                      {projectsLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                          <Loader2 size={14} style={{ color: '#f59e0b', animation: 'spin 1s linear infinite' }} />
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading projects...</span>
                        </div>
                      ) : projects.length === 0 ? (
                        <div style={{ color: '#fb7185', fontSize: 12 }}>No projects found. Create a project first.</div>
                      ) : (
                        <>
                          <select
                            value={creatingProject ? '__new__' : (selectedProjectId ?? '')}
                            onChange={(e) => {
                              if (e.target.value === '__new__') {
                                setCreatingProject(true);
                                setSelectedProjectId(null);
                              } else {
                                setCreatingProject(false);
                                setSelectedProjectId(e.target.value ? Number(e.target.value) : null);
                              }
                            }}
                            style={{
                              width: '100%', padding: '8px 12px', background: 'var(--hover-bg)',
                              border: '1px solid var(--card-border)', borderRadius: 8,
                              color: 'var(--text-primary)', fontSize: 13,
                              outline: 'none', boxSizing: 'border-box' as const,
                            }}
                          >
                            <option value="" style={{ color: 'var(--text-muted)' }}>Select a project...</option>
                            {projects.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                            <option value="__new__">+ Create new project</option>
                          </select>
                          {creatingProject && (
                            <div style={{ marginTop: 8 }}>
                              <input
                                type="text"
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                placeholder="New project name"
                                style={{
                                  width: '100%', padding: '8px 12px', background: 'var(--hover-bg)',
                                  border: '1px solid var(--card-border)', borderRadius: 8,
                                  color: 'var(--text-primary)', fontSize: 13,
                                  outline: 'none', boxSizing: 'border-box' as const,
                                }}
                                autoFocus
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div>
                    <label style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginBottom: 6 }}>File name</label>
                    <input
                      type="text"
                      value={newFileName}
                      onChange={(e) => setNewFileName(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 12px', background: 'var(--hover-bg)',
                        border: '1px solid var(--card-border)', borderRadius: 8,
                        color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                        outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              )}

              {saveType === 'attachments' && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  {attachments.length} 3D {attachments.length === 1 ? 'file' : 'files'} will be attached to molecule #{moleculeId}.
                  {scalarProps.length > 0 && ` ${scalarProps.length} scalar properties will also be merged.`}
                </div>
              )}

              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
                An import job will be created for provenance tracking.
              </div>
            </div>
          )}

          {/* ── Phase: Saving ── */}
          {phase === 'saving' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '24px 0' }}>
              <Loader2 size={28} style={{ color: '#f59e0b', animation: 'spin 1s linear infinite' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Saving {totalItems} {typeLabel.toLowerCase()}...</span>
              <div style={{ width: '100%', height: 6, background: 'var(--hover-bg)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#f59e0b', borderRadius: 3, transition: 'width 0.3s', width: `${progress}%` }} />
              </div>
              <button
                onClick={() => { abortRef.current = true; setPhase('configure'); }}
                style={{ ...btnStyle, fontSize: 12 }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* ── Phase: Done ── */}
          {phase === 'done' && resultSummary && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Check size={18} style={{ color: '#16a34a' }} />
                <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>Saved successfully</span>
              </div>
              <div style={{ background: 'var(--hover-bg)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--card-border)' }}>
                {saveType === 'properties' && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {resultSummary.properties_saved} properties saved
                    {selectedFileId && files.length > 0 ? ` to ${files.find(f => Number(f.id) === selectedFileId)?.fileName || 'file'}` : moleculeId ? ` to molecule #${moleculeId}` : ''}
                  </div>
                )}
                {saveType === 'structures' && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {resultSummary.molecules_created} molecules created
                    {effectiveProjectId && projects.length > 0 && (
                      <> in {projects.find(p => p.id === effectiveProjectId)?.name || 'project'} &gt; {newFileName}</>
                    )}
                    {!effectiveProjectId && <> in file #{resultSummary.file_id}</>}
                    {resultSummary.errors > 0 && <span style={{ color: '#fb7185' }}> ({resultSummary.errors} errors)</span>}
                  </div>
                )}
                {saveType === 'attachments' && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {resultSummary.attachments_saved} attachments saved to molecule #{moleculeId}
                  </div>
                )}
              </div>
              {resultSummary.error_details && resultSummary.error_details.length > 0 && (
                <div style={{ background: 'rgba(239,68,68,0.05)', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(239,68,68,0.2)' }}>
                  {resultSummary.error_details.map((e: string, i: number) => (
                    <div key={i} style={{ color: '#fb7185', fontSize: 11, fontFamily: 'monospace' }}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--card-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {phase === 'preview' && (
            <>
              <button onClick={onClose} style={btnStyle}>Cancel</button>
              <button onClick={() => setPhase('configure')} style={primaryBtnStyle} disabled={totalItems === 0}>
                Configure <ChevronRight size={14} />
              </button>
            </>
          )}
          {phase === 'configure' && (
            <>
              <button onClick={() => setPhase('preview')} style={btnStyle}>Back</button>
              <button onClick={handleSave} style={primaryBtnStyle} disabled={totalItems === 0 || (saveType === 'structures' && !effectiveProjectId && !(creatingProject && newProjectName.trim())) || (needsContextPicker && !selectedFileId)}>
                <Database size={14} /> Save {totalItems} {typeLabel}
              </button>
            </>
          )}
          {phase === 'done' && (
            <>
              {resultSummary?.file_id && saveType === 'structures' && (
                <button
                  onClick={() => { window.location.href = `/chemistry/file/${resultSummary.file_id}/editor`; }}
                  style={btnStyle}
                >
                  Open file
                </button>
              )}
              <button onClick={() => { onSaveComplete?.(); onClose(); }} style={primaryBtnStyle}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Styles (matching standalone workbench pages)
// ─────────────────────────────────────────────────────────────────────────

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500,
};

const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle, background: '#f59e0b', color: 'var(--content-bg, #1e1e2e)',
  border: '1px solid #f59e0b', fontWeight: 600,
};

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', background: 'var(--hover-bg)',
  border: '1px solid var(--card-border)', borderRadius: 8,
  color: 'var(--text-primary)', fontSize: 13,
  outline: 'none', boxSizing: 'border-box' as const,
};

export default SaveResultsWizard;
